import { StringOrNull } from '../entity/types';
import { ITask } from '../tasks/tasksType';
import {
  IDocumentProperties,
  ProviderItemType,
} from '../providers/providerTypes';

export type TargetGroup = 'Participants' | 'Teacher' | 'Manager';
export type TargetTime = 'StartDate' | 'EndDate';
export type ContentVisible = 'ForEver' | 'DuringPhase';
export type RoleType = 'District' | 'Post' | 'Compound' | 'InProject' | null;
export type DocumentViewTypes =
  | 'unknown'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'image'
  | 'office';
const videoFormats = [
  'video',
  'mp4',
  '3gp',
  'ts',
  'm4v',
  'webm',
  'mkv',
  'mpeg',
  'mp2',
  'mpg',
  'flv',
  'avi',
  'rm',
  'movie',
  'video/mpeg',
  'video/x-flv',
  'video/mp4',
  'application/x-mpegURL',
  'video/MP2T',
  'video/mp2t',
  'video/3gpp',
  'video/3gpp2',
  'video/quicktime',
  'video/x-m4v',
  'video/x-msvideo',
  'video/x-ms-wmv',
  'video/ogg',
  'video/webm',
  'video/x-matroska',
  'video/vnd.avi',
  'video/avi',
  'video/msvideo',
];
const audioFormats = [
  'audio',
  'm4a',
  'aac',
  'flac',
  'mid',
  'xmf',
  'mxmf',
  'rtttl',
  'rtx',
  'ota',
  'imy',
  'mp3',
  'wav',
  'ogg',
  'audio/basic',
  'audio/aac',
  'audio/ogg',
  'audio/vnd.wav',
  'audio/vorbis',
  'audio/mpeg',
  'audio/mp4',
  'audio/mid',
  'audio/midi',
  'audio/x-midi',
  'audio/mp3',
  'audio/wav',
  'audio/vnd.wave',
  'audio/wave',
  'audio/x-wav',
  'audio/webm',
  'audio/3gpp',
  'audio/3gpp2',
  'audio/MPA',
  'audio/mpa-robust',
];
const pdfFormats = ['pdf', 'application/pdf'];
export const zipFormats = [
  'zip',
  'application/zip',
  'application/x-zip-compressed',
];
export const rarFormats = ['rar', 'application/vnd.rar'];
export const excelFormats = [
  'excel',
  'xls',
  'xlsx',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
];

export const officeFormats = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/msword',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.oasis.opendocument.spreadsheet',
  'word',
  'excel',
  'docx',
  'xls',
  'doc',
  'ppt',
  'xlsx',
  'pptx',
  'office',
];

const imageFormats = [
  'photo',
  'image',
  'jpg',
  'jpeg',
  'png',
  'bmp',
  'webp',
  'gif',
  'tiff',
  'ico',
  'apng',
  'avif',
  'image/bmp',
  'image/jpeg',
  'image/pipeg',
  'image/tiff',
  'image/svg+xml',
  'image/x-ico',
  'image/gif',
  'image/png',
  'image/webp',
  'image/apng',
  'image/avif',
  'image/vnd.microsoft.icon',
  'image/vnd.adobe.photoshop',
  'image/x-bmp',
  'application/image',
];

const cache = {};

export function getViewType(
  format: StringOrNull
): DocumentViewTypes | 'zip' | 'rar' {
  if (!format) return 'unknown';
  if (cache[format]) {
    return cache[format];
  }
  let ret = 'unknown';
  if (videoFormats.find((it) => it === format)) ret = 'video';
  if (audioFormats.find((it) => it === format)) ret = 'audio';
  if (imageFormats.find((it) => it === format)) ret = 'image';
  if (pdfFormats.find((it) => it === format)) ret = 'pdf';
  if (zipFormats.find((it) => it === format)) ret = 'zip';
  // if (rarFormats.find((it) => it === format)) ret = 'rar';
  // if (excelFormats.find((it) => it === format)) ret = 'excel';
  if (officeFormats.find((it) => it === format)) ret = 'office';
  cache[format] = ret;
  return ret as DocumentViewTypes;
}

export interface IStreamingGroup {
  id: string;
  name: string;
}

export interface IDownStreamTarget {
  id: string;
  targetFolderName: string;
  companyName: string;
  companyId: number;
  providerId: number;
  targetFolderId: number;
  groups: string[];
  tagIds: number[];
  deleted: boolean;
}

export interface IDownStreamConfig {
  id: string;
  sourceFolderId: number;
  sourceFolderName: string;
  targetGroups: string[];
}

export interface IStreamingConfig {
  groups: IStreamingGroup[];
  targets: IDownStreamTarget[];
  downStreams: IDownStreamConfig[];
}

export interface ICompany {
  configFlag: { [key: string]: string };
  id: number;
  name: string;
  stub: boolean;
  owner: ICompanyMember;
  admins: Array<number>;
  roles: { [key: number]: ICompanyRole };
  permissionSchemes: { [key: number]: ICompanyPermissionScheme };
  status: string;
  members: { [key: number]: ICompanyMember };
  projects: { [key: number]: IProjectStore };
  templates: { [key: number]: ICompanyTemplate };
  relatedProviders: ICompanyProviders;
  documents: Array<IDocumentView>;
  mentionCount: {
    directAddress: number;
    info: number;
  };
  pdpPanels: CompanyPDP[];
  local?: string;
  timeZone?: string;
  domain?: string;
  limitViewToDomain?: boolean;
  streamingConfig?: IStreamingConfig;
}

export interface ICompanyServer {
  configFlag: { [key: string]: string };
  documents: Array<IDocumentView> | null;
  id: number;
  members: Array<ICompanyMember> | null;
  mentionCount: { directAddress: number; info: number };
  name: string;
  owner: ICompanyMember;
  pdpPanel: CompanyPDP | null;
  permissionSchemes: Array<ICompanyPermissionScheme> | null;
  projects: Array<IProjectStore> | null;
  roles: Array<ICompanyRole> | null;
  stub: boolean;
  templates: Array<ICompanyTemplate> | null;
}

export interface ICompanyState {
  data: { [key: number | string]: ICompany };
  status?: 'loading' | 'notLoaded' | 'loaded' | '';
}
export type EffectivityFormula = 'Advanced' | 'Simple';
export interface CompanyPDP {
  id: number;
  companyId: number;
  ownerReportToken?: string;
  properties: {
    effectivityFormula?: EffectivityFormula;
    name?: string;
    icon?: string;
    topicTags: Array<number>;
    topicTagsInPDP: Array<number>;
    classPhaseParentTag?: number;
    classHoldType?: number;
    classLocations?: number;
    classHomeFolder?: number;
    classPhaseDefaultProps?: Array<ClassPhaseProp>;
    questionBankFolder?: number;
    classProviderId?: number;
    checkClassTimeConflict?: {
      enabled: 'Off' | 'Warning' | 'Error';
      checkForHoldTypes: number[];
      checkForClassLocations: number[];
      checkForPhases: number[];
      allowOverlapMinutes: number;
    };
    outputConfigs?: PdpOutputConfig[];
  };
  managerRoleId?: number;
}

export interface PdpOutputConfig {
  name: string;
  fields: string[];
  group?: number | null;
  filters: PDPOutputFilter[];
  bottomText: string;
}
export type PDPOutputFilter = 'OnlyApproved' | 'OnlyPresent' | 'OnlyWithClass';

export interface PhaseReminder {
  localId: string;
  targetGroup: TargetGroup;
  targetTime: TargetTime;
  timeOffsetM: number;
  message?: string;
}

export type AllowChoseGroup = 'No' | 'AllowChange';

export interface ClassPhaseProp {
  phaseTagId: number;
  reminders?: Array<PhaseReminder>; // todo update it
  contentVisible: ContentVisible;
  phaseDocuments: Array<ClassDocument>;
  disabled?: boolean;
  phaseStartDates: { [key: string]: number };
  phaseEndDates: { [key: string]: number };
  location?: number;
  holdType?: number;
  limitViewToFillSurveyForPart?: number;
  limitViewToPresentMembers?: boolean;
  showCorrectAnswers?: boolean;
  phaseStageId: number;
  limitStateToOne?: boolean;
  includeInCorrective?: boolean;
  phaseWithGrade?: boolean;
  surveyTagFilters?: SurveyTagFilters;
  adminUsers?: string[];
}

type SurveyTagFilters = 'None' | 'ClassAndPhase' | 'ClassAndPhaseAndStage';
export const StageTagOffset = -1_000_000_000;
export interface ClassDocument {
  providerItemId?: number;
  title?: string;
  content?: string;
  providerId?: number;
  type?: ProviderItemType;
  itemToken?: string;
  itemChangeDataToken?: string;
  surveyToken?: string;
  surveyId?: number;
}
export interface IInvitedMember {
  name: string;
  roles: Array<string>;
  phoneNumber: string;
  inactive?: boolean;
  possibleCurrentMember?: string[];
  toDeleteRoleIds?: number[];
  employeeId?: string;
}

export interface ICompanyProvider {
  id: number;
  name: string;
  rootDocumentId: number;
}

export type ICompanyProviders = { [key: string]: ICompanyProvider };

export interface IUserCompanyRole {
  userId: number;
  roleId: number;
  sourceRoleId: number;
}

export interface ICompanyMember {
  userRoles?: IUserCompanyRole[]; // from server only
  roles: { [roleId: number]: ICompanyMemberRole };
  title?: string;
  userId: number;
  admin: boolean;
  inactive: boolean;
  companyId: number;
  signature: null | IDocumentView;
  conversationInboxId: number;
  employeeId?: string | null | undefined;
}

export interface ICompanyMemberRole {
  id: number;
  name: string;
  sourceRoleId: number;
}

export interface ICompanyTemplate {
  task?: ITask;
  taskId: number;
  children?: Array<number>;
  companyId: number;
  suggestOnDocumentManager: boolean;
}

export interface IUserRoleView {
  userId: number;
  roleId: number;
}

export interface ICompanyRole {
  id: number;
  name: string;
  displayName?: string;
  order?: number;
  stub?: boolean;
  users: Array<IUserRoleView>;
  companyId?: number;
  description?: StringOrNull;
  autoAddPrefix?: boolean | null;
  isProjectRole?: boolean;
  projectTags?: Array<string>;
  allowManualAssignToMember?: boolean;
  roleType?: RoleType;
  isUnique?: boolean;
  applyIfAllSurroundingExists?: boolean;
  compoundCreationWithRoleId?: number;
  compoundCreationIsUnique?: boolean;
  relatedTagId?: number;
  surroundingMeRoles?: Array<number>;
  roles?: { [roleId: number]: ICompanyMemberRole };
  inactive?: boolean;
}

export interface ICompanyRoleServer {
  id: number | null;
  name: string;
  order?: number;
  stub?: boolean;
  companyId?: number;
  description?: StringOrNull;
  autoAddPrefix?: boolean | null;
  users?: Array<{ userId: number; roleId: number }>;
  isProjectRole?: boolean;
  projectTags?: Array<string>;
  allowManualAssignToMember?: boolean;
  roleType?: RoleType;
  isUnique?: boolean;
  applyIfAllSurroundingExists?: boolean;
  compoundCreationWithRoleId?: number;
  compoundCreationIsUnique?: boolean;
  relatedTagId?: number;
  surroundingMeRoles?: Array<number>;
}

export interface ICompanyPermissionScheme {
  id: number;
  companyId: number;
  properties: {
    name: string;
    description: StringOrNull;
    roleIdTagIdListMap: { [key: number]: Array<number> };
  };
  autoGenerated?: boolean;
}

export interface IDocumentView {
  id?: number;
  url: string;
  deleted?: boolean;
  fileSize: number;
  uploading?: boolean;
  properties: IDocumentProperties;
  uploadDate?: number;
  documentType?: string;
  thumbnailUrl?: string;
  expirationDate?: number;
}

export interface CompanyMemberLog {
  id: number;
  userId: number;
  companyId?: number;
  actorUserId?: number;
  actionDate?: number;
  action?: CompanyMemberUpdateAction;
  props: {
    oldValue: string;
    newValue: string;
  };
}
export type CompanyMemberUpdateAction =
  | 'UpdateName'
  | 'UpdatePhone'
  | 'AddRole' // role id as string in new value
  | 'RemoveRole' // roleId as string in old value
  | 'AddedToCompany' // company id in new value
  | 'RemovedFromCompany'; // company Id in old value

export interface IProject {
  id: number;
  name: string;
  stub?: boolean;
  tags: Array<string>;
  companyId: number;
  templates?: Array<IProjectTemplate>;
  description: string;
  projectMembers?: Array<IProjectMemberView>;
  projectRoleMembers?: Array<IProjectMemberView>;
  myMembership?: IProjectMemberView;
  visibleAllTasks: boolean;
  archived: boolean;
  isLoading?: boolean;
}

export interface IProjectStore {
  id: number;
  name: string;
  stub: boolean;
  tags: Array<string>;
  companyId: number;
  templates: Array<IProjectTemplate>;
  description: string;
  projectMembers: { [userId: number]: IProjectMemberView };
  projectRoleMembers: { [roleId: number]: IProjectMemberView };
  myMembership?: IProjectMemberView;
  visibleAllTasks: boolean;
  archived: boolean;
  isLoading: boolean;
}

export type TProjects = {
  [key: number]: IProjectStore;
};

export interface IProjectMemberView {
  admin: boolean;
  canCreate: boolean;
  hidden: boolean;
  implicit: boolean;
  relatedRoleId?: number;
  projectId: number;
  projectRoleId?: number;
  title: string | undefined;
  userId?: number;
  roleId?: number;
  visibleAllTasks: boolean;
}

export type ProjectMemberConfig = Exclude<
  keyof IProjectMemberView,
  'userId' | 'hidden' | 'projectId'
>;

export interface IProjectTemplateOverrides {
  fields: { [name: string]: string };
  fieldOptions: { [name: string]: string[] };
  assigneeMemberIds: number[] | undefined;
  followerMemberIds: number[] | undefined;
  verifierMemberId: number | undefined;
  taskPublisherMemberId: number | undefined;
  subTaskPublisherMemberId: number | undefined;
}

export interface IProjectTemplate {
  task: ITask;
  taskId: number;
  projectId: number;
  allowRoot: boolean;
  overrides: IProjectTemplateOverrides;
}

export interface ClassTimeConflict {
  classId?: number;
  className?: string;
  groupKey?: string;
  groupTitle?: string;
  conflictStartDate?: Date;
  conflictEndDate?: Date;
  termIds: number[];
}

export interface ClassTimeConflictResult {
  hasConflicts: boolean;
  conflicts: ClassTimeConflict[];
}
